import { FeatureFlag, OrganizationPricingPlan } from 'api/organizationApi';
import { User, LimitedUser, UserFeatureOverride } from 'api/userApi';

export type Role =
  | 'Administrator'
  | 'ProjectManager'
  | 'Supervisor'
  | 'Technician';

export function userHasAccess(user: User, requiredRoles: Role[]) {
  return requiredRoles.includes(user.role);
}

export function canManageNoteVisibility(user: User) {
  return !user.isLimitedUser && user.role === 'Administrator';
}

export function canCreatePrivateNote(user: User) {
  return (
    !user.isLimitedUser &&
    userHasAccess(user, ['Administrator', 'ProjectManager', 'Supervisor'])
  );
}

export function canViewNoteVisibilityStatus(user: User) {
  return (
    !user.isLimitedUser &&
    userHasAccess(user, ['Administrator', 'ProjectManager', 'Supervisor'])
  );
}

export function userHasFeatureAccess(user: User, requiredFeature: FeatureFlag) {
  return (
    user.__organization__?.featureFlags?.includes(requiredFeature) ||
    user.__organization__?.earlyAccessFeatureFlags?.includes(requiredFeature)
  );
}

export function shouldShowProIcon(user: User, requiredFeature: FeatureFlag) {
  return (
    !user.__organization__?.featureFlags?.includes(requiredFeature) ||
    user.__organization__?.earlyAccessFeatureFlags?.includes(requiredFeature)
  );
}

export function isEarlyAccessFeature(user: User, requiredFeature: FeatureFlag) {
  return user.__organization__?.earlyAccessFeatureFlags?.includes(
    requiredFeature
  );
}

/**
 * Checks a per-user permission exception (see `UserFeatureOverride` on the server) —
 * a different axis from `userHasFeatureAccess` above, which reads the org's feature
 * flags. Kept as a sibling function rather than a variant, since the two read
 * different fields on `User` and should stay easy to grep apart.
 */
export function userHasOverride(user: User, override: UserFeatureOverride) {
  return user.featureOverrides?.includes(override) ?? false;
}

export function userHasFullFeatureAccess(
  user: User,
  requiredFeatures: FeatureFlag[]
): boolean {
  const userFeatures = user.__organization__?.featureFlags ?? [];

  if (requiredFeatures.some((feature) => userFeatures.includes(feature))) {
    return true;
  }

  if (!requiredFeatures.every((feature) => userFeatures.includes(feature))) {
    return false;
  }
  return false;
}

// export function orgHasAccess(
//   orgUser: User,
//   allowedPlans: OrganizationPricingPlan[]
// ) {
//   return allowedPlans.includes(orgUser.__organization__.pricingPlan);
// }

export function limitedUserHasAccess({
  user,
  route,
}: {
  user: LimitedUser;
  route: string;
}) {
  const routesOutsideJob = [
    '/',
    '/jobs',
    '/contact-support',
    `/users/profile/${user.id}`,
    '/inbox',
  ];

  if (routesOutsideJob.includes(route)) {
    return true;
  } else if (
    route.startsWith('/jobs') ||
    route.startsWith('/formbuilder-customize')
  ) {
    const jobId = route.split('/')[2]; // Extract job ID from the route
    return user.limitedUserJobsIds?.some((id) => id === jobId) || false;
  } else {
    return false;
  }
}
